import React, { useState } from "react";

import { FormHelperText, Grid, makeStyles } from "@material-ui/core";
import { ErrorMessage, Form, Formik } from "formik";
import * as yup from "yup";

import InputLabel from "./InputLabel";
import TextField from "./TextField";
import DialogActions from "./DialogActions";
import nurseModalPicutre from "../../assets/images/nurseModalPicture.png";
import rodaModalPicture from "../../assets/images/rodaModalPicture.png";
import {
  invalidEmail,
  invalidLength,
  requiredField,
} from "../../constants/messages";
import { createUser, updateUser } from "../../api/user";
import { User } from "../../types/hospital";
import { errorCodes } from "../../constants/errors";

const useStyles = makeStyles((theme) => ({
  error: {
    color: theme.palette.error.main,
  },
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(8),
  },
  img: {
    width: "13rem",
    display: "block",
    [theme.breakpoints.only("xs")]: {
      width: "10rem",
    },
  },
}));

const nurseSchema = yup.object().shape({
  name: yup.string().max(30, invalidLength).required(requiredField),
  surname: yup.string().max(30, invalidLength).required(requiredField),
  email: yup.string().email(invalidEmail).required(requiredField),
});

type UserFormProps = {
  hospitalId: number | null;
  handleClose: () => void;
  handleConfirm?: () => void;
  userUpdate: User;
  setTriggerUseEffect: () => void;
};

export default function UserForm({
  hospitalId,
  handleClose,
  handleConfirm,
  userUpdate,
  setTriggerUseEffect,
}: UserFormProps) {
  const classes = useStyles();
  const [userExistsError, setUserExistsError] = useState("");

  function handleSubmitForm(
    values: User,
    setValues: (
      values: React.SetStateAction<User>,
      shouldValidate?: boolean | undefined
    ) => void,
    handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void
  ) {
    setValues({ ...values, hospitalId: hospitalId! });
    handleSubmit();
  }

  return (
    <Formik
      initialValues={userUpdate}
      validationSchema={nurseSchema}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={async (values) => {
        if (values.id) {
          try {
            await updateUser(values);
            setTriggerUseEffect();
            handleClose();
          } catch (error: any) {
            if (
              error.response &&
              error.response.status === errorCodes.CONFLICT
            ) {
              setUserExistsError(
                "Već postoji korisnički račun s navedenom email adresom."
              );
            }
          }
        } else {
          try {
            await createUser(values);
            setTriggerUseEffect();
            if (values.hospitalId) {
              handleConfirm?.();
            } else {
              handleClose();
            }
          } catch (error: any) {
            if (
              error.response &&
              error.response.status === errorCodes.CONFLICT
            ) {
              setUserExistsError(
                "Već postoji korisnički račun s navedenom email adresom."
              );
            }
          }
        }
      }}
    >
      {({ values, setValues, handleChange, handleSubmit }) => (
        <Form>
          <div className={classes.root}>
            <img
              src={hospitalId ? nurseModalPicutre : rodaModalPicture}
              alt="nurse"
              className={classes.img}
            />
          </div>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <InputLabel>Ime:</InputLabel>
              <TextField
                autoFocus
                onChange={handleChange}
                value={values.name}
                name={"name"}
              />
              <ErrorMessage
                name={"name"}
                component={FormHelperText}
                className={classes.error}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel>Prezime:</InputLabel>
              <TextField
                onChange={handleChange}
                value={values.surname}
                name={"surname"}
              />
              <ErrorMessage
                name={"surname"}
                component={FormHelperText}
                className={classes.error}
              />
            </Grid>
          </Grid>
          <InputLabel>Email:</InputLabel>
          <TextField
            onChange={handleChange}
            value={values.email}
            fullWidth
            name={"email"}
            type="email"
          />
          <ErrorMessage
            name={"email"}
            component={FormHelperText}
            className={classes.error}
          />
          {userExistsError && (
            <FormHelperText error={true}> {userExistsError} </FormHelperText>
          )}
          <DialogActions
            handleClose={handleClose}
            handleSubmit={() =>
              handleSubmitForm(values, setValues, handleSubmit)
            }
          />
        </Form>
      )}
    </Formik>
  );
}
